import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DashboardChart, { ChartData } from './DashboardChart';
import DynamicFilterPanel, { DynamicFilterConfig } from '../../../components/DynamicComponent/DynamicFilterPanel';
import useApi from '../../../services/useApi';
import endpoints from '../../../services/endpoints';
import dayjs from 'dayjs';
import { getMNOColor } from '../../../utils/chartColors';
import { 
  forceSetMnoOptions,
  setSelectedMnos,
  selectMnoOptions,
  selectSelectedMnos
} from '../../../store/slices/systemTransactionalPerformanceSlice';
// Location utilities are handled by DynamicFilterPanel internally

// Types for API response
interface PerformanceTotals {
  total_transactions: number;
  success_transactions: number;
  failed_transactions: number;
  success_rate: number;
  failure_rate: number;
}

interface MnoWiseTotal {
  mno_id: string;
  mno_name: string;
  total_transactions: number;
}

interface LocationInfo {
  country: string;
  province?: string;
  region?: string;
}

interface AllMno {
  id: string;
  name: string;
}

interface PerformanceApiResponse {
  message: string;
  data: {
    totals: PerformanceTotals;
    mno_wise_totals: MnoWiseTotal[];
    location_info: LocationInfo;
    all_mnos?: AllMno[];
  };
}

interface SystemTransactionalPerformanceProps {
  className?: string;
}

const SystemTransactionalPerformance: React.FC<SystemTransactionalPerformanceProps> = ({ className = '' }) => {
  const api = useApi();
  const dispatch = useDispatch();
  
  // Redux state for MNO options and selections
  const mnoOptions = useSelector(selectMnoOptions);
  const selectedMnos = useSelector(selectSelectedMnos);
  
  // Track if MNO options have been set initially
  const [mnoOptionsSet, setMnoOptionsSet] = useState(false);
  
  // State for data
  const [performanceData, setPerformanceData] = useState<PerformanceApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  
  // State for filters
  const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams());
  
  // Ref to track initialization
  const hasInitialized = useRef(false);
  
  // Chart data states
  const [performanceChartData, setPerformanceChartData] = useState<ChartData>({ labels: [], datasets: [] });
  const [totalTransactionsChartData, setTotalTransactionsChartData] = useState<ChartData>({ labels: [], datasets: [] });

  // Location filter states
  const [selectedLocation, setSelectedLocation] = useState({
    country: 'Afghanistan',
    province: '',
    region: ''
  });

  // Dynamic filter configuration
  // Location data is handled by DynamicFilterPanel internally
  

  const filterConfig: DynamicFilterConfig = {
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "start_date",
        end: "end_date",
      },
    },
    mno: {
      label: "MNO Name",
      type: "checkbox",
      options: mnoOptions.length > 0 ? mnoOptions.map(mno => mno.mno_name) : loading ? ["Loading MNO options..."] : ["No MNO options available"],
      searchKey: "mno_ids",
      required: false,
      valueMapping: mnoOptions.length > 0 ? mnoOptions.reduce((acc, mno) => {
        acc[mno.mno_name] = mno.mno_id;
        return acc;
      }, {} as { [key: string]: string }) : {},
      reverseMapping: mnoOptions.length > 0 ? mnoOptions.reduce((acc, mno) => {
        acc[mno.mno_id] = mno.mno_name;
        return acc;
      }, {} as { [key: string]: string }) : {},
    },
    Location: {
      label: "Location",
      type: "dropdown",
      required: false,
      searchKey: "location",
      subFilters: {
        country: {
          label: "Country",
          placeholder: "Select Country",
          searchKey: "country"
        },
        province: {
          label: "Province",
          placeholder: "Select Province", 
          searchKey: "province"
        },
        region: {
          label: "Region", 
          placeholder: "Select Region",
          searchKey: "region"
        }
      }
    },
  };

  // Function to process performance data for charts
  const processPerformanceData = useCallback((data: PerformanceApiResponse) => {
    const { totals, mno_wise_totals } = data.data;
    
    // Performance chart data (Success/Failure rates)
    const performanceChart: ChartData = {
      labels: ['Success Rate', 'Failed Rate'],
      datasets: [{
        label: 'Transaction Performance',
        data: [Number(Number(totals.success_rate).toFixed(2)), Number(Number(totals.failure_rate).toFixed(2))],
        backgroundColor: ['#22C55E', '#EF4444'], // Green for success, Red for failure
        borderColor: ['#FFFFFF', '#FFFFFF'], // White borders
        borderWidth: 2,
      }]
    };
    
    // Total transactions chart data (MNO wise)
    const totalTransactionsChart: ChartData = {
      labels: mno_wise_totals.map((mno: MnoWiseTotal) => mno.mno_name),
      datasets: [{
        label: 'Total Transactions',
        data: mno_wise_totals.map((mno: MnoWiseTotal) => mno.total_transactions),
        backgroundColor: mno_wise_totals.map((mno: MnoWiseTotal) => getMNOColor(mno.mno_name)),
        borderColor: Array(mno_wise_totals.length).fill('#FFFFFF'), // White borders for all segments
        borderWidth: 1,
      }]
    };
    
    setPerformanceChartData(performanceChart);
    setTotalTransactionsChartData(totalTransactionsChart);
  }, []);

  // Function to fetch performance data
  const fetchPerformanceData = useCallback(async (filters: { [key: string]: string | string[] | boolean | null }) => {
    setLoading(true);
    setError(undefined);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Only add filter_type if explicitly provided
      if (filters.filter_type) {
        params.append('filter_type', filters.filter_type as string);
      }
      
      // Add MNO IDs if selected
      if (filters.mno_ids && Array.isArray(filters.mno_ids) && filters.mno_ids.length > 0) {
        params.append('mno_ids', filters.mno_ids.join(','));
      }
      
      // Add location filters (trim to avoid leading/trailing spaces becoming '+')
      if (filters.country) {
        params.append('country', String(filters.country).trim());
      }
      if (filters.province) {
        params.append('province', String(filters.province).trim());
      }
      if (filters.region) {
        params.append('region', String(filters.region).trim());
      }
      
      // Add date range for custom filter
      if (filters.filter_type === 'custom') {
        if (filters.start_date) {
          // Ensure date is in YYYY-MM-DD format using dayjs
          const formattedStartDate = dayjs(filters.start_date as string).format('YYYY-MM-DD');
          params.append('start_date', formattedStartDate);
        }
        if (filters.end_date) {
          // Ensure date is in YYYY-MM-DD format using dayjs
          const formattedEndDate = dayjs(filters.end_date as string).format('YYYY-MM-DD');
          params.append('end_date', formattedEndDate);
        }
      }
      
      // Add client timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      params.append('client_timezone', timezone);
      
      const endpoint = `${endpoints.systemTransactionalPerformance}?${params.toString()}`;
      const response = await api.getAPI(endpoint);
    
      if (response.error) {
        setError('Failed to fetch system transactional performance data');
        return;
      }
      
      const apiData = response.data;
      
      setPerformanceData(apiData);
      processPerformanceData(apiData);
      
      // Store MNO options in Redux for filter from all_mnos field (only on initial load)
      if (apiData.data.all_mnos && apiData.data.all_mnos.length > 0 && !mnoOptionsSet) {
        const mnoOptions = apiData.data.all_mnos.map((mno: AllMno) => ({
          mno_id: mno.id,
          mno_name: mno.name
        }));
        dispatch(forceSetMnoOptions(mnoOptions));
        setMnoOptionsSet(true);
      }
      
    } catch (err) {
      console.error('Error fetching performance data:', err);
      setError('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [api, processPerformanceData, dispatch, mnoOptionsSet]);

  // Filter handler
  const handleFilterApply = (filters: { [key: string]: string | string[] | boolean | null }) => {
    // Update location state
    setSelectedLocation({
      country: (filters.country as string) || 'Afghanistan',
      province: (filters.province as string) || '',
      region: (filters.region as string) || ''
    });
    
    // Set loading state
    setLoading(true);
    
    // Update internal search params
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Clear existing filter parameters
    newSearchParams.delete('start_date');
    newSearchParams.delete('end_date');
    newSearchParams.delete('mno_ids');
    newSearchParams.delete('country');
    newSearchParams.delete('province');
    newSearchParams.delete('region');
    
    // Process filters
    const processedFilters: { [key: string]: string | string[] | boolean | null } = {};
    
    // Handle date range filters
    if (filters.start_date || filters.end_date) {
      processedFilters.filter_type = 'custom';
      
      if (filters.start_date) {
        const formattedStartDate = dayjs(filters.start_date as string).format('YYYY-MM-DD');
        processedFilters.start_date = formattedStartDate;
        newSearchParams.set('start_date', formattedStartDate);
      }
      
      if (filters.end_date) {
        const formattedEndDate = dayjs(filters.end_date as string).format('YYYY-MM-DD');
        processedFilters.end_date = formattedEndDate;
        newSearchParams.set('end_date', formattedEndDate);
      }
    }
    // Don't set default filter_type - let backend handle it
    
    // Add MNO filters
    if (filters.mno_ids) {
      processedFilters.mno_ids = filters.mno_ids;
      newSearchParams.set('mno_ids', Array.isArray(filters.mno_ids) ? filters.mno_ids.join(',') : filters.mno_ids as string);
      
      // Store selected MNOs in Redux for persistence
      const selectedMnoIds = Array.isArray(filters.mno_ids) ? filters.mno_ids : [filters.mno_ids as string];
      dispatch(setSelectedMnos(selectedMnoIds));
    } else {
      // Clear selected MNOs if no MNO filter is applied
      dispatch(setSelectedMnos([]));
    }
    
    // Add location filters (trim to avoid leading '+')
    if (filters.country) {
      const country = String(filters.country).trim();
      processedFilters.country = country;
      newSearchParams.set('country', country);
    }
    if (filters.province) {
      const province = String(filters.province).trim();
      processedFilters.province = province;
      newSearchParams.set('province', province);
    }
    if (filters.region) {
      const region = String(filters.region).trim();
      processedFilters.region = region;
      newSearchParams.set('region', region);
    }
    
    // Update internal search params
    setSearchParams(newSearchParams);
    
    fetchPerformanceData(processedFilters);
  };

  // Initialize with default data
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // Let backend handle defaults - don't send any parameters
      fetchPerformanceData({});
    }
  }, [fetchPerformanceData]);

  // Create initial values for the filter panel
  const initialValues = useMemo(() => {
    const values: { [key: string]: string | string[] } = {};
    
    // Set MNO initial values if we have selected MNOs
    if (selectedMnos.length > 0 && mnoOptions.length > 0) {
      const selectedMnoNames = selectedMnos.map(mnoId => {
        const mno = mnoOptions.find(m => m.mno_id === mnoId);
        return mno ? mno.mno_name : '';
      }).filter(Boolean);
      
      if (selectedMnoNames.length > 0) {
        values.mno = selectedMnoNames;
      }
    }
    
    return values;
  }, [selectedMnos, mnoOptions]);

  return (
    <div className='p-4'>
      <h2 className="text-2xl font-semibold text-primary_text -mt-9 pb-6">System Transactional Performance Ratio</h2>
    <div className={`w-full p-4  rounded-lg border border-outline ${className}`}>
      <div className="flex items-center justify-end mb-6">
        <div className="flex items-center gap-4">
          {/* Location Display */}
          <div className="text-md text-primary_text">
            {(() => {
              const locationParts = [];
              if (selectedLocation.region) locationParts.push(selectedLocation.region);
              if (selectedLocation.province) locationParts.push(selectedLocation.province);
              if (selectedLocation.country) locationParts.push(selectedLocation.country);
              
              return locationParts.length > 0 ? locationParts.join(', ') : 'Afghanistan';
            })()}
          </div>
          
          <DynamicFilterPanel
            key={`filter-${selectedMnos.join(',')}`}
            config={filterConfig}
            onApply={handleFilterApply}
            onClear={() => {
              setSearchParams(new URLSearchParams());
              
              // Clear selected MNOs when clearing filters
              dispatch(setSelectedMnos([]));
              
              // Reset location state to default
              setSelectedLocation({
                country: 'Afghanistan',
                province: '',
                region: ''
              });
              
              // Let backend handle defaults - don't send any parameters
              fetchPerformanceData({});
            }}
            initialValues={initialValues}
            loading={loading}
            searchParams={searchParams}
            customHeight="h-[110px]"
          />
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Performance Chart */}
        <div className="w-full">
          {performanceData && performanceData.data?.totals?.success_rate === 0 && performanceData.data?.totals?.failure_rate === 0 ? (
            <div className="relative w-full bg-white rounded-lg border border-outline" style={{ height: '400px' }}>
              <div className="flex items-center justify-center p-4" style={{ height: '80px' }}>
                <h3 className="font-semibold text-primary_text" style={{ fontSize: '16px' }}>Transaction Performance</h3>
              </div>
              <div className="flex items-center justify-center" style={{ height: '320px' }}>
                <div className="text-gray-500" style={{ fontSize: '14px' }}>No Data Available</div>
              </div>
            </div>
          ) : (
            <DashboardChart
              type="doughnut"
              data={performanceChartData}
              height={400}
              chartHeight={350}
              showLegend={true}
              loading={loading}
              error={error}
              topbar={{
                title: "Transaction Performance",
                rightContent: undefined,
                className: "!justify-center"
              }}
              options={{
                // cutout: '50%', // Removed to make chart same size as Total No. of Transactions
                scales: {
                  x: { display: false },
                  y: { display: false }
                },
                plugins: {
                  legend: {
                    display: true,
                    position: 'bottom',
                    onClick: function() {
                      // Disable legend clicks completely
                      return false;
                    },
                    labels: {
                      usePointStyle: false, // Use circles like other charts
                      boxWidth: 14,
                      boxHeight: 14,
                      padding: 24,
                      font: {
                        size: 14,
                        family: 'Montserrat, sans-serif',
                        weight: '600'
                      }
                    }
                  },
                  tooltip: {
                    callbacks: {
                      title: function() {
                        return ''; // Remove the main header
                      },
                      label: function(context: { label?: string; dataIndex: number; parsed: number; chart?: { data?: { labels?: string[] } } }) {
                        const label = context.label || context.chart?.data?.labels?.[context.dataIndex] || '';
                        const value = context.parsed;
                        return ` ${label}: ${value}%`;
                      }
                    }
                  }
                }
              } as unknown as Record<string, unknown>}
            />
          )}
        </div>

        {/* Total Number of Transactions Chart */}
        <div className="w-full">
          {performanceData && (!performanceData.data?.mno_wise_totals || performanceData.data?.mno_wise_totals.length === 0) ? (
            <div className="relative w-full bg-white rounded-lg border border-outline" style={{ height: '400px' }}>
              <div className="flex items-center justify-center p-4" style={{ height: '80px' }}>
                <h3 className="font-semibold text-primary_text" style={{ fontSize: '16px' }}>Total No. of Transactions</h3>
              </div>
              <div className="flex items-center justify-center" style={{ height: '320px' }}>
                <div className="text-gray-500" style={{ fontSize: '14px' }}>No Data Available</div>
              </div>
            </div>
          ) : (
            <DashboardChart
              type="pie"
              data={totalTransactionsChartData}
              height={400}
              chartHeight={308}
              showLegend={false}
              loading={loading}
              error={error}
              topbar={{
                title: "Total No. of Transactions",
                rightContent: undefined,
                className: "!justify-center"
              }}
              belowChart={
                performanceData && (
                  <div className="text-center">
                    <div className="text-primary_text opacity-60 -mt-6" style={{ fontSize: '14px', fontFamily: 'Montserrat, sans-serif', fontWeight: '700' }}>
                      Overall: {performanceData.data?.totals?.total_transactions}
                    </div>
                  </div>
                )
              }
              belowTopbarHeight={60}
              options={{
                // cutout: '50%',
                scales: {
                  x: { display: false },
                  y: { display: false }
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      title: function() {
                        return ''; // Remove the main header
                      },
                      label: function(context: { label?: string; dataIndex: number; parsed: number; chart?: { data?: { labels?: string[] } } }) {
                        // For pie charts, the label comes from the chart's labels array
                        const label = context.label || context.chart?.data?.labels?.[context.dataIndex] || '';
                        const value = context.parsed;
                        return ` ${label}: ${value}`;
                      }
                    }
                  }
                }
              } as unknown as Record<string, unknown>}
            />
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default SystemTransactionalPerformance;
